import { createColumnHelper } from "@tanstack/react-table"
import type { ColumnDef } from "@tanstack/react-table"
import type { EngagementPeopleRow } from "@/types/report"

const columnHelper = createColumnHelper<EngagementPeopleRow>()

export const engagementPeopleColumns: ColumnDef<EngagementPeopleRow, any>[] = [
  columnHelper.accessor("userProfileId", {
    header: "Profile ID",
    size: 120,
    meta: { type: "integer" },
    enableHiding: true,
  }),
  columnHelper.accessor("workerName", {
    header: "Worker Name",
    size: 200,
    meta: { type: "string", uniqueValues: true },
  }),
  columnHelper.accessor("workerType", {
    header: "Worker Type",
    size: 150,
    meta: { type: "string", uniqueValues: true },
  }),
  columnHelper.accessor("functionalManager", {
    header: "Functional Manager",
    size: 190,
    meta: { type: "string", uniqueValues: true },
  }),
  columnHelper.accessor("lineOfBusiness", {
    header: "Line of Business",
    size: 170,
    meta: { type: "string", uniqueValues: true },
  }),
  columnHelper.accessor("procurementSource", {
    header: "Procurement Source",
    size: 180,
    meta: { type: "string", uniqueValues: true },
  }),
  columnHelper.accessor("profileStatus", {
    header: "Status",
    size: 130,
    meta: { type: "status", uniqueValues: true },
  }),
  columnHelper.accessor("engagementUtilizationPercent", {
    header: "Utilization %",
    size: 150,
    meta: { type: "percent" },
  }),
  columnHelper.accessor("plannedHours", {
    header: "Planned Hours",
    size: 160,
    meta: { type: "number" },
  }),
  columnHelper.accessor("totalApprovedHoursToDate", {
    header: "Approved Hours",
    size: 160,
    meta: { type: "number" },
  }),
  columnHelper.accessor("vacationTakenToDate", {
    header: "Vacation Taken",
    size: 160,
    meta: { type: "number" },
  }),
  columnHelper.accessor("skills", {
    header: "Skills",
    size: 250,
    meta: { type: "string" },
  }),
  columnHelper.accessor("certifications", {
    header: "Certifications",
    size: 250,
    meta: { type: "string" },
  }),
  columnHelper.accessor("jobTitle", {
    header: "Job Title",
    size: 180,
    meta: { type: "string" },
  }),
  columnHelper.accessor("workerLocation", {
    header: "Worker Location",
    size: 180,
    meta: { type: "string", uniqueValues: true },
  }),
  columnHelper.accessor("payRate", {
    header: "Pay Rate",
    size: 130,
    meta: { type: "currency" },
  }),
  columnHelper.accessor("totalBillableHours", {
    header: "Total Billable Hours",
    size: 170,
    meta: { type: "number" },
  }),
  columnHelper.accessor("totalNonBillableHours", {
    header: "Total Non-Billable Hours",
    size: 195,
    meta: { type: "number" },
  }),
  // GW-363: both dates come from the worker's own profile, replacing one conflated
  // "Hire / Engagement ..." pair that meant employment dates for T4 salaried workers and
  // engagement dates for everyone else.
  columnHelper.accessor("hireDate", {
    header: "Hire Date",
    size: 150,
    meta: { type: "date" },
  }),
  columnHelper.accessor("employmentEndDate", {
    header: "Employment End Date",
    size: 190,
    meta: { type: "date" },
  }),
  // T4 salaried only, so blank on every non-T4 row and on salaried workers who were never
  // converted. Unpaired: it is a single event, not the end of a range.
  columnHelper.accessor("conversionDate", {
    header: "Conversion Date",
    size: 170,
    meta: { type: "date" },
  }),
  columnHelper.accessor("tenureMonths", {
    header: "Tenure (Months)",
    size: 150,
    // Not additive: months of tenure added up across a group is not a figure, and the column is
    // null for a worker with no hire date, which would blank the group's total.
    meta: { type: "integer", excludeFromGroupTotal: true },
  }),
  // The worker's engagement span across every engagement they are on, not one engagement's own
  // dates. Paired as a range, since together they describe one window.
  columnHelper.accessor("firstEngagementStartDate", {
    header: "First Engagement Start Date",
    size: 215,
    meta: { type: "date", dateRangePair: "lastEngagementEndDate" },
  }),
  columnHelper.accessor("lastEngagementEndDate", {
    header: "Last Engagement End Date",
    size: 205,
    meta: { type: "date", dateRangePair: "firstEngagementStartDate" },
  }),
  columnHelper.accessor("timeEntryStartDate", {
    header: "Time Entry Start Date",
    size: 190,
    meta: { type: "date" },
  }),
  columnHelper.accessor("timeEntryEndDate", {
    header: "Time Entry End Date",
    size: 190,
    meta: { type: "date" },
  }),
  columnHelper.accessor("currency", {
    header: "Currency",
    size: 110,
    meta: { type: "string", uniqueValues: true },
  }),
]

export const defaultHiddenColumns: Record<string, boolean> = {
  userProfileId: false,
}
